<template>
  <div class="lib__form" data-cy="temporary-connection-form">
    <header class="lib__form__header">
      <!-- Module TITRE -->
      <main-title>{{ $t('common.temporaryConnectionRequest') }}</main-title>
      <!-- Fin Module TITRE -->

      <!-- Module BreadCrumb -->
      <breadcrumb :currentStep="currentStep" :lastStep="lastStep" :lastStepSeen="lastStepSeen" :stepDefinitions="stepDefinitions" @goToStep="goToStep" @reset="resetForm" :isLastStepActive="false"></breadcrumb>
      <!-- Fin Module BreadCrumb -->
    </header>

    <DialogBox />
    <ApiErrors />

    <back-to-summary :summaryStepNumber="5" :isButtonShowed="currentStep < 5 && lastStepSeen == 5" @backToSummary="goToStepWithValidation" />

    <!-- Module Step1 : Adresse -->
    <AddressStep @canContinue="canContinue = $event" @next="nextStep" ref="step1" v-if="currentStep == 1"></AddressStep>
    <!-- Fin Module Step1 : Adresse -->

    <!-- Module Step2 : Demande -->
    <ConfigurationInfoStep @next="nextStep" @previous="previousStep" ref="step2" v-if="currentStep == 2"></ConfigurationInfoStep>
    <!-- Fin Module Step2 : Demande -->

    <!-- Module Step8 : Contact -->
    <ContactInfoStep @next="nextStep" @previous="previousStep" ref="step3" v-if="currentStep == 3"></ContactInfoStep>
    <!-- Fin Module Step8 : Contact -->

    <!-- Module Step9 : Info TVA -->
    <VatInfoStep @next="nextStep" @previous="previousStep" ref="step4" v-if="currentStep == 4"></VatInfoStep>
    <!-- Fin Module Step9 : Info TVA -->

    <send-confirmation :contact-info="contactInfoState" :title="$t('common.temporaryConnectionRequest')" v-if="currentStep == 6"></send-confirmation>
    <!-- Module Step10 : Résumé -->
    <SummaryStep :read-only="currentStep == 6" @goToStep="goToStep" @next="nextStep" @previous="previousStep" ref="step5" v-if="currentStep == 5 || currentStep == 6"></SummaryStep>
    <!-- Fin Module Step10 : Résumé -->

    <StepActions :can-continue="canContinue" :has-next="currentStep < 6" :has-previous="currentStep > 1 && currentStep < 6" :has-print="currentStep == 6" :has-reset="lastStepSeen > 1" :loading="loading" @next="tryNextStep" @previous="tryPreviousStep" @reset="resetForm"></StepActions>
  </div>
</template>

<script lang="ts">
import { Watch, Component } from 'vue-property-decorator'
import { ContactInfo } from '@/models'
import StepActions from '@/components/shared/StepActions.vue'
import SendConfirmation from '@/components/display/SendConfirmation.vue'
import Step from '@/components/steps/Step'
import AddressStep from '@/components/steps/temporary-connection/AddressStep.vue'
import ConfigurationInfoStep from '@/components/steps/temporary-connection/ConfigurationInfoStep.vue'
import ContactInfoStep from '@/components/steps/temporary-connection/ContactInfoStep.vue'
import VatInfoStep from '@/components/steps/temporary-connection/VatInfoStep.vue'
import SummaryStep from '@/components/steps/temporary-connection/SummaryStep.vue'
import DialogBox from '@/components/shared/DialogBox.vue'
import { State, Action, Mutation } from 'vuex-class'
import { Form } from './Form'
import { StepDefinition } from '@ores/vue-library'
import ApiErrors from '@/components/display/ApiErrors.vue'
import { removeInterceptors } from '@/plugins/http-interceptors-global'
import { addWorksResponseInterceptors } from '@/plugins/http-response-interceptors-works'
import store from '@/store/index'

@Component({
  components: {
    StepActions,
    AddressStep,
    ConfigurationInfoStep,
    ContactInfoStep,
    VatInfoStep,
    SummaryStep,
    SendConfirmation,
    DialogBox,
    ApiErrors
  },
  name: 'temporary-connection'
})
export default class TemporaryConnection extends Form {
  //#region [Data]
  public stepDefinitions: StepDefinition[] = [
    new StepDefinition('address', 1),
    new StepDefinition('request', 2),
    new StepDefinition('contact', 3),
    new StepDefinition('vat', 4),
    new StepDefinition('summary', 5),
    new StepDefinition('confirm', 6)
  ] as StepDefinition[]

  @State('contactInfo', { namespace: 'temporaryConnection/contactInfo' })
  public readonly contactInfoState!: ContactInfo | null
  //#endregion

  //#region [Method]
  @Action('validate', { namespace: 'temporaryConnection/vatInfo' })
  public validateVatInfoSelection!: (contactInfo: ContactInfo) => Promise<boolean>

  public mounted() {
    this.currentStep = 1
    this.lastStepSeen = 1
    this.stepToGoAfterValidation = 5
    this.stepsToValidate = [3]

    this.onLangChanged(this.lang)

    if (this.interceptorId !== null) {
      removeInterceptors(this.$api, this.interceptorId)
    }
    const interceptorId = addWorksResponseInterceptors(this.$api, store)
    this.setInterceptorId(interceptorId)

    // Check maintenance
    this.$api.get('api/maintenanceManagement/temporary-connection-request')
  }

  public getStepComponent(step: number): Step | null {
    switch (this.currentStep) {
      case 1:
        return this.$refs.step1 as Step
      case 2:
        return this.$refs.step2 as Step
      case 3:
        return this.$refs.step3 as Step
      case 4:
        return this.$refs.step4 as Step
      case 5:
        return this.$refs.step5 as Step
      default:
        return null
    }
  }

  public resetForm() {
    this.currentStep = 0
    this.lastStepSeen = 1
    this.$store.dispatch('temporaryConnection/reset')
    this.$nextTick(() => (this.currentStep = 1))
  }
  //#endregion

  //#region [Watch]
  @Watch('contactInfoState', { deep: true })
  public async onContactInfoChanged(contactInfo: ContactInfo | null) {
    if (contactInfo === null) {
      return
    }

    this.ensureLastStep(4, await this.validateVatInfoSelection(contactInfo))
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('afterEnsured'))
    }, 100);
  }
  //#endregion
}
</script>